import type Notification from '../models/notification.js';
import type { NotificationCategory, NotificationPriority } from '../models/notification.js';
import type { Toast } from '../ltn-toast/ltn-toast-service.js';

interface ReadWriter {
  set(path: string, value: unknown): unknown;
}

export const NOTIFICATION_CATEGORY_LABELS: Record<NotificationCategory, string> = {
  JOBS: 'Jobs',
  CONNECTIONS: 'Connections',
  DATA_ACCESS: 'Data access',
  RELATIONSHIPS: 'Relationships',
  PUBLIC_LEDGER: 'Public ledger',
  ACCOUNT: 'Account',
};

const PRIORITY_MARKERS: Partial<Record<NotificationPriority, string>> = { HIGH: 'High', URGENT: 'Urgent' };

type Linkable = Pick<Notification, 'entity'>;

// Where a notification opens in the app; null for one about something with no page
export function notificationPath({ entity }: Linkable): string | null {
  if (!entity?.id) return null;
  switch (entity.collectionName) {
    case 'comRelationship':
      return `/relationships/${entity.id}`;
    case 'company':
      return `/company/${entity.id}`;
    case 'job':
      return `/feed/job/${entity.id}`;
    case 'workflow':
      return `/workflows/${entity.id}`;
    case 'companyWorkflow':
      return `/workflow-studio/${entity.id}`;
    case 'plTransaction':
    case 'plCreditPurchase':
      return '/my-account/public-ledger';
    default:
      return null;
  }
}

export interface NotificationAction {
  id: 'CLAIM_JOB';
  label: string;
}

// Mirrors the tags ns-lambda-ts's notification catalogue sends; connection answers stay in their respond job
const NOTIFICATION_ACTIONS: Partial<Record<string, NotificationAction[]>> = {
  JOB_ASSIGNED_TO_TEAM: [{ id: 'CLAIM_JOB', label: 'Claim' }],
  JOB_IN_POOL: [{ id: 'CLAIM_JOB', label: 'Claim' }],
};

// What can be done straight from an unread notification, acting on its entity
export function notificationActions({
  tag,
  readAt,
  entity,
}: Pick<Notification, 'tag' | 'readAt' | 'entity'>): NotificationAction[] {
  if (readAt || !tag || !entity?.id) return [];
  return NOTIFICATION_ACTIONS[tag] ?? [];
}

export function priorityMarker(priority: NotificationPriority | null | undefined): string | null {
  return (priority && PRIORITY_MARKERS[priority]) ?? null;
}

// A toast is for something that arrived while the app was open, for this person, and not already read
export function isFreshArrival(
  notification: Pick<Notification, 'personId' | 'readAt' | 'createdAt'>,
  personId: string | undefined,
  since: number,
): boolean {
  if (!personId || notification.personId !== personId || notification.readAt) return false;
  return Date.parse(notification.createdAt) >= since;
}

export function notificationToast(notification: Notification, open: (() => void) | null): Toast {
  const urgent = notification.priority === 'URGENT';
  return {
    label: notification.title,
    ...(notification.description ? { description: notification.description } : {}),
    ...(urgent ? { variant: 'danger' as const } : {}),
    ...(open ? { actionLabel: 'View', action: open } : {}),
    timeout: notification.priority === 'HIGH' ? 10000 : 6000,
  };
}

// A user may write only readAt on their own notifications, one at a time
export function markNotificationsRead(
  db: ReadWriter | undefined,
  notifications: Pick<Notification, 'id' | 'readAt'>[],
) {
  if (!db) return;
  const readAt = new Date().toISOString();
  for (const notification of notifications)
    if (!notification.readAt) db.set(`notification.${notification.id}.readAt`, readAt);
}

export interface NotificationsFilter {
  personId: string;
  unreadOnly: boolean;
  category: NotificationCategory | null;
}

export function notificationsQuery(filter: NotificationsFilter): Record<string, unknown> {
  return {
    personId: { $eq: filter.personId },
    ...(filter.unreadOnly ? { readAt: { $eq: null } } : {}),
    ...(filter.category ? { category: { $eq: filter.category } } : {}),
  };
}
